import { addWeeks, compareIsoWeeks, formatIsoWeekKey } from './iso-week.js';
import type { IsoWeek } from './iso-week.js';
import type { ProjectStatus } from './project-fields.js';

/**
 * Restaufwand und Fertigstellungsgrad melden (AC-601) — die EINE Stelle für
 * gültigen Stand, Kommentarpflicht, Rückfrage und Drift.
 *
 * Eine Meldung ist append-only (ADR-10): jede Schätzung ist eine neue Zeile in
 * `remaining_effort_reports`, nie ein Überschreiben. Welche Meldung für eine
 * Woche gilt, entscheidet allein `gueltigeMeldung`; der Server prüft mit
 * `pruefeMeldung`, die Oberfläche zeigt dieselben Hinweise vorab.
 * Entscheidungen in docs/stories/AC-601/ENTSCHEIDUNGEN.md; die Nummern
 * (E3 …) verweisen dorthin.
 *
 * Stunden sind Dezimalzeichenketten und werden in Hundertstelstunden als
 * Ganzzahl verglichen, nie über `Number` (Kernregel 10).
 *
 * Transportfrei (ADR-04) und OHNE Zod: die Oberfläche lädt die Datei über
 * `@asas/domain/restaufwand`. Die Schemata stehen in `work-packages.ts`.
 */

/** Restaufwand als Eingabe: wie Plan-Stunden, `NUMERIC(7,2)`, ab 0. */
export const REST_STUNDEN_PATTERN = /^\d{1,5}(?:\.\d{1,2})?$/;

export const RESTAUFWAND_KOMMENTAR_MAX_LENGTH = 1000;

/** Wie weit eine Meldung höchstens zurückreicht (E6), in Wochen vor der laufenden. */
export const RESTAUFWAND_RUECKGRIFF_WOCHEN = 26;

/**
 * Wie weit eine Belegung vorausreicht, die zum Melden berechtigt (AC-107-1, E4):
 * wer in den naechsten acht Wochen eingeplant ist, darf schon melden.
 */
export const RESTAUFWAND_BELEGUNG_VORAUS_WOCHEN = 8;

/**
 * Die Wochen, in denen eine freigegebene Belegung zum Melden berechtigt
 * (AC-107-1, E4): vom Rueckgriff der Bezugswoche (E6) bis acht Wochen voraus,
 * beide einschliesslich.
 */
export function belegungsFenster(heute: IsoWeek): { readonly von: IsoWeek; readonly bis: IsoWeek } {
  return {
    von: addWeeks(heute, -RESTAUFWAND_RUECKGRIFF_WOCHEN),
    bis: addWeeks(heute, RESTAUFWAND_BELEGUNG_VORAUS_WOCHEN),
  };
}

/** Was eine Meldung für den Vergleich braucht. */
export interface Meldestand {
  readonly bezugswoche: IsoWeek;
  readonly restHours: string;
}

/** `'12.5'` → `1250n`. Nur für Zeichenketten in der Form von `REST_STUNDEN_PATTERN`. */
function hundertstel(stunden: string): bigint {
  if (!REST_STUNDEN_PATTERN.test(stunden)) {
    throw new RangeError(`Keine Stundenangabe: ${stunden}`);
  }
  const [ganz = '0', bruch = ''] = stunden.split('.');
  return BigInt(ganz) * 100n + BigInt(bruch.padEnd(2, '0'));
}

/**
 * Die Meldung, die für `woche` gilt (E6): die mit der jüngsten Bezugswoche bis
 * einschließlich `woche`; bei gleicher Bezugswoche die später gemeldete.
 *
 * VORAUSSETZUNG: `meldungen` steht in Meldereihenfolge (`gemeldet_am`, dann
 * `id`), so wie der Server die Historie liest. Die spätere gewinnt deshalb über
 * ihre Position, nicht über einen Zeitstempelvergleich im Browser.
 */
export function gueltigeMeldung<T extends Meldestand>(
  meldungen: readonly T[],
  woche: IsoWeek,
): T | null {
  let gilt: T | null = null;
  for (const meldung of meldungen) {
    if (compareIsoWeeks(meldung.bezugswoche, woche) > 0) continue;
    if (gilt === null || compareIsoWeeks(meldung.bezugswoche, gilt.bezugswoche) >= 0) {
      gilt = meldung;
    }
  }
  return gilt;
}

/** Der aktuelle Stand (E6): die Meldung mit der jüngsten Bezugswoche. Gleiche Voraussetzung. */
export function aktuelleMeldung<T extends Meldestand>(meldungen: readonly T[]): T | null {
  let gilt: T | null = null;
  for (const meldung of meldungen) {
    if (gilt === null || compareIsoWeeks(meldung.bezugswoche, gilt.bezugswoche) >= 0) {
      gilt = meldung;
    }
  }
  return gilt;
}

/**
 * Ist der neue Restaufwand eine Erhöhung (E3)? Verglichen wird mit dem
 * gültigen Stand zur Bezugswoche der neuen Meldung, ohne Vorgänger mit den
 * Plan-Stunden. Ohne beides — erste Meldung, „noch nicht geplant“ — gibt es
 * nichts zu erhöhen.
 */
export function istErhoehung(eingabe: {
  readonly restHours: string;
  readonly vorgaenger: Meldestand | null;
  readonly planHours: string | null;
}): boolean {
  const vergleich = eingabe.vorgaenger?.restHours ?? eingabe.planHours;
  return vergleich !== null && hundertstel(eingabe.restHours) > hundertstel(vergleich);
}

/**
 * Drift (E4): mehr Restaufwand als Plan-Stunden beim Melden. Ohne Plan keine
 * Drift. AC-606 und AC-604 erweitern DIESE Funktion um Ist-Stunden; deshalb
 * wird Drift nie gespeichert.
 */
export function istDrift(meldung: {
  readonly restHours: string;
  readonly planHoursBeiMeldung: string | null;
}): boolean {
  return (
    meldung.planHoursBeiMeldung !== null &&
    hundertstel(meldung.restHours) > hundertstel(meldung.planHoursBeiMeldung)
  );
}

/** Eine Meldung mit ihrem Konto — nur am Server, die Oberfläche kennt keine Konten (E11). */
export interface KontoMeldestand extends Meldestand {
  readonly konto: string;
  readonly fertigstellungsgrad: number;
}

/**
 * Die Bezugswochen mit Widerspruch (AC-601-1, E1), als `formatIsoWeekKey`.
 *
 * Je Woche zählt je Konto dessen jüngste Meldung; ein Widerspruch besteht,
 * wenn sich diese Stände zwischen mindestens zwei Konten in Reststunden oder
 * Fertigstellungsgrad unterscheiden — jede Abweichung, ohne Schwelle. Gleicht
 * eine Seite an, ist er erledigt. Die Gewinnerregel bleibt `gueltigeMeldung`:
 * ein Widerspruch wird angezeigt (und mit AC-416 gemeldet), nicht aufgelöst.
 *
 * VORAUSSETZUNG wie bei `gueltigeMeldung`: Meldereihenfolge.
 */
export function widerspruchsWochen(meldungen: readonly KontoMeldestand[]): Set<string> {
  const jeWoche = new Map<string, Map<string, KontoMeldestand>>();

  for (const meldung of meldungen) {
    const woche = formatIsoWeekKey(meldung.bezugswoche);
    const jeKonto = jeWoche.get(woche) ?? new Map<string, KontoMeldestand>();
    jeKonto.set(meldung.konto, meldung);
    jeWoche.set(woche, jeKonto);
  }

  const wochen = new Set<string>();
  for (const [woche, jeKonto] of jeWoche) {
    const staende = new Set(
      [...jeKonto.values()].map(
        (stand) => `${String(hundertstel(stand.restHours))}|${String(stand.fertigstellungsgrad)}`,
      ),
    );
    if (staende.size > 1) wochen.add(woche);
  }

  return wochen;
}

/** 100 % fertig, aber noch Restaufwand (E5): nur nach ausdrücklicher Bestätigung. */
export function verlangtRueckfrage(meldung: {
  readonly restHours: string;
  readonly fertigstellungsgrad: number;
}): boolean {
  return meldung.fertigstellungsgrad === 100 && hundertstel(meldung.restHours) > 0n;
}

/** Getrimmt; leer heißt „kein Kommentar“. */
export function kommentarNormalisiert(kommentar: string | null | undefined): string | null {
  const getrimmt = kommentar?.trim() ?? '';
  return getrimmt === '' ? null : getrimmt;
}

/** Früheste zulässige Bezugswoche (E6), gezählt von der laufenden Woche. */
export function fruehesteBezugswoche(heute: IsoWeek): IsoWeek {
  return addWeeks(heute, -RESTAUFWAND_RUECKGRIFF_WOCHEN);
}

/** Was der Projektstatus einer Meldung erlaubt (E9). Maßgeblich ist der heutige Status. */
export const RESTAUFWAND_JE_STATUS: Readonly<Record<ProjectStatus, 'melden' | 'lesen' | 'keine'>> =
  {
    akquise: 'keine',
    aktiv: 'melden',
    abgeschlossen: 'lesen',
    archiviert: 'lesen',
    verloren: 'keine',
  };

/** Warum der Server eine Meldung abweist. Die Oberfläche übersetzt, der Server nicht. */
export const RESTAUFWAND_ABWEISUNGEN = [
  'projekt_nicht_beauftragt',
  'bezugswoche_in_zukunft',
  'bezugswoche_zu_alt',
  'kommentar_fehlt',
  'rueckfrage_fertig_mit_rest',
] as const;
export type RestaufwandAbweisung = (typeof RESTAUFWAND_ABWEISUNGEN)[number];

/**
 * Alle fachlichen Prüfungen einer Meldung, in fester Reihenfolge. Die Rückfrage
 * (E5) kommt zuletzt: sie soll nur erscheinen, wenn sonst alles stimmt, damit
 * eine Bestätigung nicht ins Leere geht.
 */
export function pruefeMeldung(
  eingabe: {
    readonly restHours: string;
    readonly fertigstellungsgrad: number;
    readonly bezugswoche: IsoWeek;
    readonly kommentar?: string | null | undefined;
    readonly bestaetigtTrotzFertig?: boolean | undefined;
  },
  kontext: {
    readonly heute: IsoWeek;
    readonly projektStatus: ProjectStatus;
    /** Gültiger Stand zur Bezugswoche OHNE die neue Meldung (`gueltigeMeldung`). */
    readonly vorgaenger: Meldestand | null;
    readonly planHours: string | null;
  },
): RestaufwandAbweisung | null {
  if (RESTAUFWAND_JE_STATUS[kontext.projektStatus] !== 'melden') {
    return 'projekt_nicht_beauftragt';
  }
  if (compareIsoWeeks(eingabe.bezugswoche, kontext.heute) > 0) return 'bezugswoche_in_zukunft';
  if (compareIsoWeeks(eingabe.bezugswoche, fruehesteBezugswoche(kontext.heute)) < 0) {
    return 'bezugswoche_zu_alt';
  }
  if (
    kommentarNormalisiert(eingabe.kommentar) === null &&
    istErhoehung({
      restHours: eingabe.restHours,
      vorgaenger: kontext.vorgaenger,
      planHours: kontext.planHours,
    })
  ) {
    return 'kommentar_fehlt';
  }
  if (eingabe.bestaetigtTrotzFertig !== true && verlangtRueckfrage(eingabe)) {
    return 'rueckfrage_fertig_mit_rest';
  }
  return null;
}

/**
 * Die Meldung ist fachlich abgewiesen. Die API antwortet mit `BAD_REQUEST` und
 * gibt `grund` aus — nur über diese Klasse, dieselbe Linie wie
 * `AnfrageBereitsEntschiedenError` (AC-405).
 */
export class RestaufwandAbgewiesenError extends Error {
  constructor(readonly grund: RestaufwandAbweisung) {
    super(`Restaufwandsmeldung abgewiesen: ${grund} (AC-601).`);
    this.name = 'RestaufwandAbgewiesenError';
  }
}
